/** @jsxImportSource @emotion/react */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@Button';
import Text from '@Text';
import FetchStatus from '../../Data/FetchStatus';
import { getClientApiLocationsAction } from '../../Hooks/actionCreators';
import {
  goToNextScreen,
  resetSolar,
  setSelectedLocation,
  setSelectedSlot,
  setStepState,
} from '../../Lib/redux/slices/clientApiJourney';
import { updateRecalculateRequiredWidgetHeight } from '../../Lib/redux/slices/dynamicHeight';
import { getPostcodeComponent } from '../__helpers__/component.helpers';
import makeAddressListStyles, {
  ADDRESS_LIST_MAX_HEIGHT,
} from './makeAddressListStyles';
import {
  getById,
  getByTestId,
  outerHeight,
  useOlbScrollMaxHeight,
  journeyProgressHeight,
} from './clientApiLayout.helpers';
import { isBookableLocation } from '../../Helpers/clientApiLocations.helpers';
import SolarSummaryCard from './SolarSummaryCard';
import useClientApiCopy from './copy/useClientApiCopy';

const AddressList = ({ stepKey }) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const classes = makeAddressListStyles({ theme });
  const copy = useClientApiCopy().address;
  const rowsRef = useRef(null);

  const isDynamicHeightEnabled = useSelector(
    (store) => store.optionalFormConfig?.isDynamicHeightEnabled
  );
  const postcode = useSelector(
    (store) =>
      getPostcodeComponent({ components: store.components })?.input?.value
  );
  const locationsStatus = useSelector(
    (store) => store.clientApiJourney.location?.listStatus
  );
  const locations = useSelector(
    (store) => store.clientApiJourney.location?.list
  );
  const persistedLocationId = useSelector(
    (store) => store.clientApiJourney.location?.selected?.id
  );

  const [selectedId, setSelectedId] = useState(persistedLocationId ?? null);

  const addresses = useMemo(
    () => (locations ?? []).filter(isBookableLocation),
    [locations]
  );
  const selectedLocation = addresses.find(
    (location) => location.id === selectedId
  );

  useEffect(() => {
    if (locationsStatus !== FetchStatus.NOT_STARTED) return;
    dispatch(getClientApiLocationsAction({ postcode }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDynamicHeightEnabled) {
      dispatch(
        updateRecalculateRequiredWidgetHeight({
          isWidgetHeightRecalculationRequired: true,
        })
      );
    } else {
      window.dispatchEvent(new Event('resize'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsStatus, selectedId]);

  const focusRow = (index) => {
    rowsRef.current?.querySelectorAll('[role="radio"]')?.[index]?.focus();
  };

  const handleRowKeyDown = (event, index) => {
    const next = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[
      event.key
    ];
    if (!next) return;
    event.preventDefault();
    const target = (index + next + addresses.length) % addresses.length;
    setSelectedId(addresses[target].id);
    focusRow(target);
  };

  const handleContinue = () => {
    dispatch(setSelectedLocation({ selectedLocation }));
    dispatch(setSelectedSlot({ selectedSlot: null }));
    if (selectedId !== persistedLocationId) {
      dispatch(resetSolar());
    }
    dispatch(setStepState({ stepKey, state: { location: selectedLocation } }));
    dispatch(goToNextScreen());
  };

  useOlbScrollMaxHeight(
    rowsRef,
    () => {
      const formContent = getById('formContentContainer');
      if (!formContent) return 0;
      const available =
        formContent.clientHeight -
        (formContent.children?.[0]?.clientHeight ?? 0) -
        journeyProgressHeight() -
        outerHeight(getByTestId('addressListTitle')) -
        outerHeight(getByTestId('addressListSubheading')) -
        outerHeight(getByTestId('addressListSelectorLabel')) -
        outerHeight(getByTestId('addressListSummaryRow')) -
        outerHeight(getByTestId('addressListContinueRow')) -
        (getById('footerContainer')?.clientHeight ?? 0);
      return Math.min(available, ADDRESS_LIST_MAX_HEIGHT);
    },
    [addresses, selectedId, isDynamicHeightEnabled],
    !isDynamicHeightEnabled
  );

  // Until there are addresses to pick from, keep the loading view: an empty or
  // failed lookup is replaced by ClientApiJourney's failure notice, and the
  // outgoing fade would otherwise show an empty address page before it.
  if (locationsStatus !== FetchStatus.COMPLETED || addresses.length === 0) {
    return (
      <Container
        css={classes.messageContainer}
        data-testid="addressListLoading"
      >
        <Text variant="text">{copy.loading}</Text>
      </Container>
    );
  }

  return (
    <Container css={classes.listContainer}>
      <Box css={classes.heading} data-testid="addressListTitle">
        <Text variant="question">{copy.heading}</Text>
      </Box>
      <Box css={classes.subheading} data-testid="addressListSubheading">
        <Text variant="text">{copy.subheading}</Text>
      </Box>
      <Box css={classes.selectorLabel} data-testid="addressListSelectorLabel">
        <Text variant="text">{copy.selectorLabel}</Text>
      </Box>
      <Box
        css={classes.rowScrollArea}
        ref={rowsRef}
        role="radiogroup"
        aria-label={copy.selectorLabel}
      >
        {addresses.map((location, index) => (
          <Box
            key={location.id}
            component="button"
            type="button"
            role="radio"
            aria-checked={location.id === selectedId}
            tabIndex={location.id === (selectedId ?? addresses[0]?.id) ? 0 : -1}
            css={[
              classes.row,
              location.id === selectedId && classes.rowSelected,
            ]}
            data-testid={`addressRow-${location.id}`}
            data-stripe={index % 2 === 0 ? 'odd' : 'even'}
            onClick={() => setSelectedId(location.id)}
            onKeyDown={(event) => handleRowKeyDown(event, index)}
          >
            <Text variant="text">{location.address}</Text>
          </Box>
        ))}
      </Box>
      <Box data-testid="addressListSummaryRow">
        <SolarSummaryCard
          rows={[
            {
              label: selectedLocation ? copy.selectedLabel : copy.postcodeLabel,
              value: selectedLocation ? selectedLocation.address : postcode,
              valueTestId: 'solarSummaryValue',
            },
          ]}
        />
      </Box>
      <Box css={classes.continueContainer} data-testid="addressListContinueRow">
        <Button
          label={copy.continue}
          onClick={handleContinue}
          disabled={!selectedLocation}
          dataTestId="addressListContinue"
        />
      </Box>
    </Container>
  );
};

export default AddressList;
